import {ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from "@angular/core";
import {StockItem} from "../../../shared/view-model/store-management/stock-item.type";
import {FetchStockItemsResult, StockItemService} from "../../services/stock-item-service/stock-item.service";
import {ActivatedRoute, Router} from "@angular/router";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {FormBuilder} from "@angular/forms";
import {SHOW_PARENTS_DEFAULT} from "../../../configuration";
import {ApolloQueryObservable} from "apollo-angular";
import {AbstractListView} from "../../../core/visbos-core/ui/components/abstract-list-view";
import {GenericModalComponent} from "../../../core/visbos-core/ui/forms/generic-modal/generic-modal.component";
import {NewStockViewComponent} from "../../stock/new-stock-view/new-stock-view.component";
import {StockInput} from "../../services/stock-input.type";
import {StockService} from "../../services/stock.service";
import {SessionActions} from "../../../shared/actions/session.actions";
import {MessageType} from "../../../shared/redux-store/session/session.types";
import {Product} from "../../../shared/view-model/store-management/product.type";
import {Packing} from "../../../shared/view-model/store-management/packing.type";
import {DatatableComponent} from "../../../core/visbos-core/ui/components/datatable/datatable.component";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {FileService} from '../../../core/visbos-core/http/file.service';
import * as _ from 'lodash';
import {StockItemStateService} from './stock-item-state.service';

@Component({
  selector: 'vb-stock-item-list-view',
  templateUrl: './stock-item-list-view.component.html',
  styleUrls: ['./stock-item-list-view.component.css']
})
export class StockItemListViewComponent extends AbstractListView implements OnInit, OnDestroy {

  // Table templates

  @ViewChild('stocksCell')
  stocksCell: TemplateRef<any>;

  @ViewChild('addStockModal')
  addStockModal: GenericModalComponent;

  @ViewChild('stockForm')
  stockForm: NewStockViewComponent;

  @ViewChild('orderItemDetails')
  articleDetails: TemplateRef<any>;

  @ViewChild('stockItemsTable')
  table: DatatableComponent;

  editable: { [cuid: string]: boolean };

  messages = {
    emptyMessage: 'Keine Artikel vorhanden',
    totalMessage: 'Artikel insgesamt'
  };

  showStockItemDetailsProxy = (row, expanded, event) => this.showStockItemDetails.apply(this, [row, expanded, event]);

  private stockItems$: ApolloQueryObservable<FetchStockItemsResult>;
  stockItems: StockItem[] = [];

  showParent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(SHOW_PARENTS_DEFAULT);

  currentStockItem: StockItem;

  constructor(private stockItemService: StockItemService,
              private stockService: StockService,
              private router: Router,
              private route: ActivatedRoute,
              private fb: FormBuilder,
              private fileService: FileService,
              private state: StockItemStateService,
              sessionActions: SessionActions, cd: ChangeDetectorRef) {

    super(sessionActions, cd);

    this.actions = [
      {label: 'Neuen Artikel anlegen', icon: 'fa-plus', action: () => this.addNewArticle.apply(this)},
      {
        label: 'Artikelliste exportieren',
        icon: 'fa-file-export',
        helpText: 'Liste aller Artikel des Standorts und der übergeordneten Standorte als Excel-Datei exportieren',
        action: () => this.exportStockItemList.apply(this)
      }
    ];

  }

  ngOnInit() {

    this.isLoading = true;

    this.searchTerm = this.fb.control(null);

    this.dataSubscriptions.push(
      this.searchTerm.valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(500)
      ).subscribe((term: any) => {
        this.state.searchTerm = term;
        this.filterItems(term, true); // if the user types a search term, reset the page to the first page as it is unknown, if the page setting still fits the filtered item list
      })
    );

    // If a search term is set in the state, patch the form control and filter the items without changing the page
    if (this.state.searchTerm) {
      this.searchTerm.patchValue(this.state.searchTerm, {emitEvent: false});
      this.filterItems(this.state.searchTerm, false);
    }

    this.stockItems$ = this.stockItemService.fetchStockItems({withParents: this.showParent$});
    this.addComponentData(this.stockItems$);
    this.addSubscription(this.stockItems$.subscribe(res => {
      this.stockItems = res.data.fetchStockItems;
      this.filterItems(this.searchTerm.value, false);
      this.isLoading = res.loading;
    }));

    this.columns = [
      {prop: 'label', name: 'Bezeichner'},
      {prop: 'category.label', name: 'Artikelkategorie'},
      {prop: 'administration_unit.label', name: 'Verwaltungseinheit'},
      {prop: 'manufacturer.label', name: 'Hersteller'},
      {prop: 'supplier.label', name: 'Lieferant'},
      //{name: 'Bestände in Lagern', cellTemplate: this.stocksCell},
    ];

  }

  registerItemsClicked(cuid: string): void {
    this.router.navigate([cuid, "gegenstaende", "registrieren"], {
      relativeTo: this.route
    });
  }

  addNewArticle(): void {
    this.router.navigate(["./neu"], {relativeTo: this.route})
  }

  deleteArticle(cuid: string) {
    this.stockItemService.deleteStockItem(cuid);
  }

  editArticle(cuid: string) {
    this.router.navigate([cuid, "edit"], {
      relativeTo: this.route
    });
  }

  addStock(stockItem) {
    this.addStockModal.show();
    this.cd.detectChanges();
    this.stockForm.control.get('stock_item_id').setValue(stockItem.cuid);
  }

  saveStock(stock: StockInput) {
    this.dataSubscriptions.push(this.stockService.createStock(stock).subscribe((res: any) => {
        this.sessionActions.pushMessage('Bestand erfolgreich hinzugefügt!', MessageType.SUCCESS, 'Bestandsverwaltung');
      },
      err => this.sessionActions.throwError(err, "Fehler beim Anlegen des Bestands, bitte wenden Sie sich an den Support!", "Bestandsverwaltung"))
    );
  }

  showStockItemDetails(row, expanded, event: Event) {

    if (!expanded) {
      let stockItemCuid = row.cuid;
      this.dataSubscriptions.push(this.stockItemService.getStockItem(stockItemCuid, true)
        .subscribe((res: any) => {
          this.currentStockItem = res;
        }, err => {
          this.currentStockItem = null;
        })
      );
    } else {
      this.currentStockItem = null;
    }
    this.table.table.rowDetail.toggleExpandRow(row);
  }

  isUbdOver(ubd: Date) {
    if (new Date(ubd) < new Date()) {
      return true;
    }
    return false;
  }

  filterItems(searchTerm, resetPage = false) {

    if (resetPage) {
      this.table.resetToFirstPage();
    }

    if (!searchTerm || searchTerm == "") {
      this.rows = this.stockItems;
    } else {
      if (this.stockItems) {
        let st = searchTerm.trim().toLowerCase();
        this.rows = this.stockItems.filter((si: StockItem) => {
          let label = this.findStringInTable(si, this.columns, st);
          if (label) {
            return true;
          }

          let inKeyWords = si.keywords && si.keywords.find(k => _.get(k, 'label', '') ? _.get(k, 'label', '').toLowerCase().includes(st) : false);
          if (inKeyWords) {
            return true;
          }

          // Search for article no
          if (si.products) {
            let artNos = [];
            si.products.forEach((prod: Product) => {

              // Check supplier and manufacturer names
              if ((_.get(prod, 'manufacturer.label', '') ? _.get(prod, 'manufacturer.label', '').toLowerCase().includes(st) : false) ||
                (_.get(prod, 'supplier.label', '') ? _.get(prod, 'supplier.label', '').toLowerCase().includes(st) : false)) {
                return true;
              }

              // Check product packings
              prod.packings.forEach((pack: Packing) => {
                if (pack.article_number ? pack.article_number.toLowerCase().includes(st) : false) {
                  return true;
                }
              })
            });
          }

          return (_.get(si, 'stock_item.visbos_article_number', '') ? _.get(si, 'stock_item.visbos_article_number', '').toLowerCase().includes(st) : false) ||
            (_.get(si, 'stock_item.category.label', '') ? _.get(si, 'stock_item.category.label', '').toLowerCase().includes(st) : false);

        });
      }
    }
  }

  private exportStockItemList() {

    this.sessionActions.pushMessage("Artikelliste wird generiert, dies kann einen Moment dauern...", MessageType.INFO, "Artikelliste erzeugen");

    this.stockItemService.generateStockItemListExport(this.activeAdminUnitCuid).subscribe(response => {
      this.fileService.saveResponseToFileSystem(response);
    }, err => {
      this.sessionActions.throwError(err, "Fehler beim Erzeugen der Datei!");
    });

  }

}




// WEBPACK FOOTER //
// ./src/app/store-management/stock-item/stock-item-list-view/stock-item-list-view.component.ts